<template>
  <div class="tab-content audit-overview-page">
    <div class="audit-hero">
      <div>
        <div class="audit-eyebrow">{{ $t('controlCenter.auditDashboard.eyebrow') }}</div>
        <h1>{{ $t('controlCenter.auditDashboard.title') }}</h1>
        <p>{{ $t('controlCenter.auditDashboard.subtitle') }}</p>
      </div>
      <button class="btn btn-primary audit-refresh" :disabled="loading" @click="loadDashboard">
        <i :class="loading ? 'fas fa-circle-notch fa-spin' : 'fas fa-rotate-right'"></i>
        {{ $t('controlCenter.common.refresh') }}
      </button>
    </div>

    <div v-if="loadError" class="overview-alert">
      <i class="fas fa-triangle-exclamation"></i>
      <span>{{ loadError }}</span>
    </div>

    <div v-loading="loading" class="overview-body">
      <section class="executive-grid">
        <div v-for="card in executiveCards" :key="card.key" class="executive-card" :class="card.tone">
          <div class="metric-top">
            <span>{{ card.label }}</span>
            <i :class="card.icon"></i>
          </div>
          <strong>{{ formatNumber(card.value) }}</strong>
          <small>{{ card.subtitle }}</small>
        </div>
      </section>

      <section class="overview-grid">
        <div class="overview-panel panel-wide panel-tall">
          <div class="panel-header">
            <div>
              <h2>{{ $t('controlCenter.auditDashboard.sections.orderMovement') }}</h2>
              <span>{{ $t('controlCenter.auditDashboard.sections.orderMovementSub') }}</span>
            </div>
          </div>
          <div class="trend-chart">
            <svg viewBox="0 0 720 260" preserveAspectRatio="none">
              <defs>
                <linearGradient id="auditTrendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#0ea5e9" stop-opacity="0.28" />
                  <stop offset="100%" stop-color="#0ea5e9" stop-opacity="0.02" />
                </linearGradient>
              </defs>
              <g class="grid-lines">
                <line v-for="line in 5" :key="line" x1="24" x2="696" :y1="line * 42" :y2="line * 42" />
              </g>
              <polygon v-if="trendPoints.length" :points="areaPoints" fill="url(#auditTrendFill)" />
              <polyline v-if="trendPoints.length" :points="linePoints" class="trend-line" />
              <circle v-for="point in trendPoints" :key="point.key" :cx="point.x" :cy="point.y" r="4" class="trend-dot" />
            </svg>
            <div class="trend-labels">
              <span v-for="item in trendLabels" :key="item">{{ item }}</span>
            </div>
          </div>
        </div>

        <div class="overview-panel panel-tall">
          <div class="panel-header">
            <div>
              <h2>{{ $t('controlCenter.auditDashboard.sections.auditHealth') }}</h2>
              <span>{{ $t('controlCenter.auditDashboard.sections.auditHealthSub') }}</span>
            </div>
          </div>
          <div class="health-score">
            <div class="score-ring" :style="scoreStyle">
              <div>
                <strong>{{ healthScore }}%</strong>
                <span>{{ $t('controlCenter.auditDashboard.labels.healthScore') }}</span>
              </div>
            </div>
          </div>
          <div class="status-stack">
            <div v-for="item in statusBreakdown" :key="item.name" class="status-row">
              <span>{{ statusLabel(item.name) }}</span>
              <strong>{{ formatNumber(item.value) }} / {{ item.percentage }}%</strong>
              <div class="mini-bar"><em :style="{ width: normalPercent(item.percentage) + '%' }"></em></div>
            </div>
          </div>
        </div>

        <div class="overview-panel panel-tall">
          <div class="panel-header">
            <div>
              <h2>{{ $t('controlCenter.auditDashboard.sections.revenueMix') }}</h2>
              <span>{{ formatCurrency(overview.totalRevenue) }}</span>
            </div>
          </div>
          <div class="donut-list">
            <div v-for="item in revenueByPlan" :key="item.name" class="donut-row">
              <div class="donut" :style="donutStyle(item.percentage)">{{ item.percentage }}%</div>
              <div>
                <strong>{{ planLabel(item.name) }}</strong>
                <span>{{ formatCurrency(item.amount) }}</span>
              </div>
            </div>
            <div v-if="!revenueByPlan.length" class="empty-note">{{ $t('controlCenter.auditDashboard.empty.noData') }}</div>
          </div>
        </div>

        <div class="overview-panel panel-wide">
          <div class="panel-header">
            <div>
              <h2>{{ $t('controlCenter.auditDashboard.sections.capabilityComposition') }}</h2>
              <span>{{ $t('controlCenter.auditDashboard.sections.capabilityCompositionSub') }}</span>
            </div>
          </div>
          <div class="composition-grid">
            <div v-for="item in compositionCards" :key="item.key" class="composition-item">
              <i :class="item.icon"></i>
              <span>{{ item.label }}</span>
              <strong>{{ formatNumber(item.value) }}</strong>
            </div>
          </div>
        </div>

        <div class="overview-panel">
          <div class="panel-header">
            <div>
              <h2>{{ $t('controlCenter.auditDashboard.sections.entitlementHealth') }}</h2>
              <span>{{ $t('controlCenter.auditDashboard.sections.entitlementHealthSub') }}</span>
            </div>
          </div>
          <div class="progress-list">
            <div v-for="item in entitlementStatus" :key="item.name" class="progress-row">
              <div>
                <span>{{ statusLabel(item.name) }}</span>
                <strong>{{ formatNumber(item.value) }}</strong>
              </div>
              <div class="mini-bar"><em :style="{ width: normalPercent(item.percentage) + '%' }"></em></div>
            </div>
          </div>
        </div>

        <div class="overview-panel">
          <div class="panel-header">
            <div>
              <h2>{{ $t('controlCenter.auditDashboard.sections.methodMix') }}</h2>
              <span>{{ $t('controlCenter.auditDashboard.sections.methodMixSub') }}</span>
            </div>
          </div>
          <div class="method-list">
            <div v-for="item in paymentMethodMix" :key="item.name">
              <span>{{ paymentMethodLabel(item.name) }}</span>
              <strong>{{ item.percentage }}%</strong>
              <em>{{ formatNumber(item.value) }}</em>
            </div>
            <div v-if="!paymentMethodMix.length" class="empty-note">{{ $t('controlCenter.auditDashboard.empty.noData') }}</div>
          </div>
        </div>

        <div class="overview-panel panel-wide">
          <div class="panel-header">
            <div>
              <h2>{{ $t('controlCenter.auditDashboard.sections.standardDemand') }}</h2>
              <span>{{ $t('controlCenter.auditDashboard.sections.standardDemandSub') }}</span>
            </div>
          </div>
          <div class="rank-list">
            <div v-for="item in standardDemand" :key="item.name" class="rank-row">
              <span>{{ item.name }}</span>
              <strong>{{ formatNumber(item.value) }}</strong>
              <div class="mini-bar"><em :style="{ width: normalPercent(item.percentage) + '%' }"></em></div>
            </div>
            <div v-if="!standardDemand.length" class="empty-note">{{ $t('controlCenter.auditDashboard.empty.noData') }}</div>
          </div>
        </div>

        <div class="overview-panel table-panel">
          <div class="panel-header">
            <div>
              <h2>{{ $t('controlCenter.auditDashboard.sections.recentOrders') }}</h2>
              <span>{{ $t('controlCenter.auditDashboard.sections.recentOrdersSub') }}</span>
            </div>
          </div>
          <div class="compact-table">
            <div v-for="order in recentOrders" :key="order.orderNo" class="table-row">
              <div>
                <strong>{{ order.orderNo }}</strong>
                <span>{{ order.companyName || '-' }}</span>
              </div>
              <div>
                <em>{{ statusLabel(order.status) }}</em>
                <span>{{ formatCurrency(order.finalAmount, order.displayCurrency) }}</span>
              </div>
            </div>
            <div v-if="!recentOrders.length" class="empty-note">{{ $t('controlCenter.auditDashboard.empty.noOrders') }}</div>
          </div>
        </div>

        <div class="overview-panel table-panel">
          <div class="panel-header">
            <div>
              <h2>{{ $t('controlCenter.auditDashboard.sections.expiringEntitlements') }}</h2>
              <span>{{ $t('controlCenter.auditDashboard.sections.expiringEntitlementsSub') }}</span>
            </div>
          </div>
          <div class="compact-table">
            <div v-for="item in expiringEntitlements" :key="item.orderNo" class="table-row">
              <div>
                <strong>{{ item.companyName || item.orderNo }}</strong>
                <span>{{ planLabel(item.planId) }}</span>
              </div>
              <div>
                <em>{{ formatNumber(item.daysRemaining) }} {{ $t('controlCenter.auditDashboard.labels.days') }}</em>
                <span>{{ formatDate(item.expiresAt) }}</span>
              </div>
            </div>
            <div v-if="!expiringEntitlements.length" class="empty-note">{{ $t('controlCenter.auditDashboard.empty.noExpiring') }}</div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { paymentAdminApi } from '@/api/paymentAdmin'
import { formatPrice } from '@/utils/currency'

const emptyDashboard = {
  overview: {
    totalOrders: 0,
    successfulOrders: 0,
    pendingOrders: 0,
    failedOrders: 0,
    activeEntitlements: 0,
    expiringEntitlements: 0,
    totalRevenue: 0,
    totalAuditorSeats: 0,
    totalAdminSeats: 0,
    catalogItems: 0,
    enabledCatalogItems: 0,
    invoiceUploadedOrders: 0
  },
  trend: [],
  statusBreakdown: [],
  paymentMethodMix: [],
  revenueByPlan: [],
  standardDemand: [],
  entitlementStatus: [],
  recentOrders: [],
  expiringEntitlements: []
}

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: false,
      loadError: '',
      dashboard: { ...emptyDashboard }
    }
  },
  computed: {
    overview() {
      return this.dashboard.overview || emptyDashboard.overview
    },
    statusBreakdown() {
      return this.dashboard.statusBreakdown || []
    },
    paymentMethodMix() {
      return this.dashboard.paymentMethodMix || []
    },
    revenueByPlan() {
      return this.dashboard.revenueByPlan || []
    },
    standardDemand() {
      return this.dashboard.standardDemand || []
    },
    entitlementStatus() {
      return this.dashboard.entitlementStatus || []
    },
    recentOrders() {
      return this.dashboard.recentOrders || []
    },
    expiringEntitlements() {
      return this.dashboard.expiringEntitlements || []
    },
    healthScore() {
      if (!this.overview.totalOrders) return 0
      const successRate = (this.overview.successfulOrders / this.overview.totalOrders) * 70
      const invoiceRate = (this.overview.invoiceUploadedOrders / this.overview.totalOrders) * 15
      const entitlementRate = this.overview.successfulOrders ? (this.overview.activeEntitlements / this.overview.successfulOrders) * 15 : 0
      return Math.round(Math.min(100, successRate + invoiceRate + entitlementRate))
    },
    scoreStyle() {
      return { background: `conic-gradient(#0ea5e9 0 ${this.healthScore}%, rgba(14, 165, 233, 0.12) ${this.healthScore}% 100%)` }
    },
    executiveCards() {
      return [
        { key: 'orders', label: this.$t('controlCenter.auditDashboard.cards.totalOrders'), value: this.overview.totalOrders, subtitle: this.$t('controlCenter.auditDashboard.cards.orderMix', { success: this.formatNumber(this.overview.successfulOrders), pending: this.formatNumber(this.overview.pendingOrders) }), icon: 'fas fa-clipboard-check', tone: 'tone-blue' },
        { key: 'revenue', label: this.$t('controlCenter.auditDashboard.cards.totalRevenue'), value: this.formatCurrency(this.overview.totalRevenue), subtitle: this.$t('controlCenter.auditDashboard.cards.failedOrders', { count: this.formatNumber(this.overview.failedOrders) }), icon: 'fas fa-chart-line', tone: 'tone-cyan' },
        { key: 'entitlements', label: this.$t('controlCenter.auditDashboard.cards.activeEntitlements'), value: this.overview.activeEntitlements, subtitle: this.$t('controlCenter.auditDashboard.cards.expiring', { count: this.formatNumber(this.overview.expiringEntitlements) }), icon: 'fas fa-shield-halved', tone: 'tone-teal' },
        { key: 'seats', label: this.$t('controlCenter.auditDashboard.cards.auditorSeats'), value: this.overview.totalAuditorSeats, subtitle: this.$t('controlCenter.auditDashboard.cards.adminSeats', { count: this.formatNumber(this.overview.totalAdminSeats) }), icon: 'fas fa-users-gear', tone: 'tone-slate' },
        { key: 'catalog', label: this.$t('controlCenter.auditDashboard.cards.catalogItems'), value: this.overview.enabledCatalogItems, subtitle: this.$t('controlCenter.auditDashboard.cards.catalogTotal', { count: this.formatNumber(this.overview.catalogItems) }), icon: 'fas fa-layer-group', tone: 'tone-indigo' },
        { key: 'invoice', label: this.$t('controlCenter.auditDashboard.cards.invoiceCoverage'), value: `${this.invoiceCoverage}%`, subtitle: this.$t('controlCenter.auditDashboard.cards.invoiceUploaded', { count: this.formatNumber(this.overview.invoiceUploadedOrders) }), icon: 'fas fa-file-invoice', tone: 'tone-navy' }
      ]
    },
    invoiceCoverage() {
      if (!this.overview.totalOrders) return 0
      return Math.round((this.overview.invoiceUploadedOrders * 1000) / this.overview.totalOrders) / 10
    },
    compositionCards() {
      return [
        { key: 'orders', label: this.$t('controlCenter.auditDashboard.labels.orders'), value: this.overview.totalOrders, icon: 'fas fa-receipt' },
        { key: 'success', label: this.$t('controlCenter.auditDashboard.labels.successOrders'), value: this.overview.successfulOrders, icon: 'fas fa-circle-check' },
        { key: 'auditors', label: this.$t('controlCenter.auditDashboard.labels.auditorSeats'), value: this.overview.totalAuditorSeats, icon: 'fas fa-user-shield' },
        { key: 'admins', label: this.$t('controlCenter.auditDashboard.labels.adminSeats'), value: this.overview.totalAdminSeats, icon: 'fas fa-user-tie' },
        { key: 'catalog', label: this.$t('controlCenter.auditDashboard.labels.enabledCatalog'), value: this.overview.enabledCatalogItems, icon: 'fas fa-tags' },
        { key: 'invoices', label: this.$t('controlCenter.auditDashboard.labels.invoices'), value: this.overview.invoiceUploadedOrders, icon: 'fas fa-file-lines' }
      ]
    },
    trendPoints() {
      const rows = this.dashboard.trend || []
      const max = Math.max(1, ...rows.map(item => Number(item.orderCount || 0)))
      const step = rows.length > 1 ? 672 / (rows.length - 1) : 0
      return rows.map((item, index) => ({
        key: item.date || index,
        x: 24 + step * index,
        y: 220 - (Number(item.orderCount || 0) / max) * 170
      }))
    },
    linePoints() {
      return this.trendPoints.map(point => `${point.x},${point.y}`).join(' ')
    },
    areaPoints() {
      if (!this.trendPoints.length) return ''
      return `24,228 ${this.linePoints} 696,228`
    },
    trendLabels() {
      const rows = this.dashboard.trend || []
      return rows.map(item => this.shortDate(item.date))
    }
  },
  created() {
    this.loadDashboard()
  },
  methods: {
    async loadDashboard() {
      this.loading = true
      this.loadError = ''
      try {
        const data = await paymentAdminApi.getAuditOverview()
        this.dashboard = Object.assign({}, emptyDashboard, data || {})
      } catch (error) {
        this.loadError = this.$t('controlCenter.auditDashboard.loadError')
      } finally {
        this.loading = false
      }
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString()
    },
    formatCurrency(value, currency) {
      const display = currency || 'RMB'
      return `${display} ${formatPrice(Number(value || 0) / 100)}`
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString()
    },
    shortDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value).slice(5)
      return `${date.getMonth() + 1}/${date.getDate()}`
    },
    normalPercent(value) {
      return Math.max(0, Math.min(100, Number(value || 0)))
    },
    donutStyle(percentage) {
      const value = this.normalPercent(percentage)
      return { background: `conic-gradient(#0ea5e9 0 ${value}%, rgba(14, 165, 233, 0.12) ${value}% 100%)` }
    },
    statusLabel(status) {
      const key = status || 'UNKNOWN'
      const translated = this.$t(`controlCenter.statusLabels.${key}`)
      return translated === `controlCenter.statusLabels.${key}` ? key : translated
    },
    paymentMethodLabel(method) {
      const key = method || 'unknown'
      const translated = this.$t(`controlCenter.auditDashboard.paymentMethods.${key}`)
      return translated === `controlCenter.auditDashboard.paymentMethods.${key}` ? key : translated
    },
    planLabel(planId) {
      if (!planId) return this.$t('controlCenter.common.unknown')
      const translated = this.$t(`controlCenter.auditDashboard.plans.${planId}`)
      return translated === `controlCenter.auditDashboard.plans.${planId}` ? planId : translated
    }
  }
}
</script>

<style scoped>
.audit-overview-page {
  color: #0f172a;
}

.audit-hero {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 16px;
  padding: 24px;
  border-radius: 8px;
  color: #fff;
  background: linear-gradient(135deg, #0649b8 0%, #0a7fd7 54%, #03b7b0 100%);
  box-shadow: 0 20px 44px rgba(6, 73, 184, 0.2);
  overflow: hidden;
}

.audit-eyebrow {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.86);
}

.audit-hero h1 {
  margin: 6px 0 8px;
  font-size: 30px;
  font-weight: 800;
  color: #fff;
}

.audit-hero p {
  margin: 0;
  max-width: 760px;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.6;
}

.audit-refresh {
  align-self: flex-end;
  white-space: nowrap;
}

.overview-alert {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid rgba(220, 38, 38, 0.18);
  border-radius: 8px;
  color: #b91c1c;
  background: #fff1f2;
}

.executive-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.executive-card,
.overview-panel {
  border-radius: 8px;
}

.executive-card {
  min-height: 128px;
  padding: 15px;
  color: #fff;
  background: linear-gradient(145deg, #0649b8, #0a7fd7);
  box-shadow: 0 14px 32px rgba(6, 73, 184, 0.14);
  overflow: hidden;
  position: relative;
}

.executive-card::after {
  content: '';
  position: absolute;
  right: -48px;
  bottom: -62px;
  width: 136px;
  height: 136px;
  border-radius: 50%;
  border: 22px solid rgba(255, 255, 255, 0.11);
}

.metric-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.86);
}

.metric-top i {
  font-size: 20px;
}

.executive-card strong {
  display: block;
  margin-top: 18px;
  font-size: 30px;
  line-height: 1;
  color: #fff;
}

.executive-card small {
  display: block;
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.78);
}

.tone-cyan { background: linear-gradient(145deg, #0a7fd7, #03b7b0); }
.tone-teal { background: linear-gradient(145deg, #0f766e, #0a7fd7); }
.tone-slate { background: linear-gradient(145deg, #334155, #0a7fd7); }
.tone-indigo { background: linear-gradient(145deg, #0649b8, #4f46e5); }
.tone-navy { background: linear-gradient(145deg, #0f172a, #0649b8); }

.overview-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 14px;
}

.overview-panel {
  grid-column: span 3;
  min-height: 304px;
  padding: 16px;
  border: 1px solid rgba(10, 127, 215, 0.15);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.075);
}

.panel-wide {
  grid-column: span 6;
}

.panel-tall {
  min-height: 372px;
}

.table-panel {
  grid-column: span 3;
  min-height: 360px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(10, 127, 215, 0.1);
}

.panel-header h2 {
  margin: 0 0 5px;
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
}

.panel-header span {
  font-size: 12px;
  color: #64748b;
}

.trend-chart {
  height: 292px;
}

.trend-chart svg {
  width: 100%;
  height: 250px;
}

.grid-lines line {
  stroke: rgba(10, 127, 215, 0.12);
  stroke-dasharray: 6 6;
}

.trend-line {
  fill: none;
  stroke: #0a7fd7;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.trend-dot {
  fill: #fff;
  stroke: #03b7b0;
  stroke-width: 3;
}

.trend-labels {
  display: grid;
  grid-template-columns: repeat(14, minmax(0, 1fr));
  gap: 4px;
  color: #64748b;
  font-size: 11px;
}

.health-score {
  display: flex;
  justify-content: center;
  padding: 12px 0 18px;
}

.score-ring,
.donut {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.score-ring {
  width: 146px;
  height: 146px;
}

.score-ring > div,
.donut::before {
  background: #fff;
}

.score-ring > div {
  width: 112px;
  height: 112px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.score-ring strong {
  font-size: 28px;
  color: #0649b8;
}

.score-ring span {
  font-size: 12px;
  color: #64748b;
}

.status-stack,
.progress-list,
.rank-list,
.method-list,
.compact-table {
  display: grid;
  gap: 10px;
}

.status-row,
.progress-row,
.rank-row {
  display: grid;
  gap: 7px;
}

.status-row {
  font-size: 12px;
  color: #64748b;
}

.status-row > span,
.progress-row span,
.rank-row span {
  color: #334155;
  font-weight: 700;
}

.status-row strong,
.progress-row strong,
.rank-row strong {
  color: #0f172a;
  font-size: 12px;
}

.mini-bar {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(14, 165, 233, 0.12);
}

.mini-bar em {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #0649b8, #03b7b0);
}

.donut-list {
  display: grid;
  gap: 14px;
}

.donut-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.donut {
  width: 68px;
  height: 68px;
  color: #0649b8;
  font-size: 13px;
  font-weight: 800;
  box-shadow: inset 0 0 0 12px rgba(255, 255, 255, 0.78);
}

.donut-row strong,
.donut-row span {
  display: block;
}

.donut-row strong {
  color: #0f172a;
}

.donut-row span {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
}

.composition-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.composition-item {
  min-height: 98px;
  padding: 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid rgba(10, 127, 215, 0.1);
}

.composition-item i {
  color: #0a7fd7;
  font-size: 20px;
}

.composition-item span,
.composition-item strong {
  display: block;
}

.composition-item span {
  margin-top: 10px;
  color: #64748b;
  font-size: 12px;
}

.composition-item strong {
  margin-top: 4px;
  color: #0f172a;
  font-size: 24px;
}

.progress-row > div:first-child,
.rank-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.method-list > div,
.table-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(10, 127, 215, 0.1);
}

.method-list span,
.method-list em,
.table-row span,
.table-row em {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-style: normal;
}

.method-list strong {
  color: #0649b8;
  font-size: 20px;
}

.table-row strong {
  color: #0f172a;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.table-row > div:last-child {
  text-align: right;
  white-space: nowrap;
}

.empty-note {
  padding: 26px 8px;
  color: #94a3b8;
  text-align: center;
  font-size: 13px;
}

@media (max-width: 1280px) {
  .executive-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .overview-panel,
  .table-panel {
    grid-column: span 6;
  }

  .panel-wide {
    grid-column: span 12;
  }
}

@media (max-width: 980px) {
  .executive-grid,
  .composition-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .overview-panel,
  .table-panel,
  .panel-wide {
    grid-column: span 12;
  }
}

@media (max-width: 768px) {
  .audit-hero {
    flex-direction: column;
    padding: 18px;
  }

  .executive-grid,
  .composition-grid {
    grid-template-columns: 1fr;
  }

  .overview-panel,
  .table-panel,
  .panel-wide {
    grid-column: span 12;
  }

  .table-row {
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .audit-hero {
    gap: 14px;
    padding: 16px;
  }

  .audit-hero h1 {
    font-size: 22px;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .audit-hero p {
    font-size: 13px;
    line-height: 1.55;
  }

  .audit-refresh {
    width: 100%;
    align-self: stretch;
    justify-content: center;
  }

  .executive-card {
    min-height: auto;
    padding: 14px;
  }

  .executive-card strong {
    margin-top: 14px;
    font-size: 25px;
    overflow-wrap: anywhere;
  }

  .overview-grid {
    gap: 12px;
  }

  .overview-panel {
    min-height: auto;
    padding: 14px;
  }

  .panel-tall,
  .table-panel {
    min-height: auto;
  }

  .panel-header {
    flex-direction: column;
    gap: 4px;
  }

  .trend-chart {
    height: 228px;
  }

  .trend-chart svg {
    height: 196px;
  }

  .trend-labels {
    grid-template-columns: repeat(7, minmax(0, 1fr));
    font-size: 10px;
  }

  .score-ring {
    width: 128px;
    height: 128px;
  }

  .score-ring > div {
    width: 98px;
    height: 98px;
  }

  .donut-row,
  .method-list > div,
  .table-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .table-row > div:last-child {
    width: 100%;
    text-align: left;
    white-space: normal;
  }
}
</style>
